// ABOUTME: HTML cell renderer for PIM data tables, previewing the content and editing it inline.
// ABOUTME: In edit mode a popover hosts the EditNode Quill field, which needs app services the design system cannot reach.
import { EditIcon, IconButton, Popover, Stack } from '@ds/coolshop';
import {
    type CustomCellRendererProps,
    type NodeContentValueO,
    useCellEditCommit,
    useEditingCellReporter,
} from '@ds/data-table';
import HtmlFieldElement from '@src/components/formFields/HtmlFieldElement';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { StandaloneField, writeLanguageEntry } from './StandaloneField';

export default function GridCellHtmlRender(props: CustomCellRendererProps) {
    const { value, contentsValue, contexts, row, editableOpt, ct, DefaultRenderer } = props;
    const intl = useIntl();

    const anchorRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [entries, setEntries] = useState(value as NodeContentValueO[]);

    const { commit, isLoading } = useCellEditCommit({
        editableOpt,
        contentsValue,
        nodeId: row.id,
        hierarchy: row.hierarchy,
        ct,
        row,
    });
    // An open editor is a pending edit: another user's save of this cell becomes a conflict.
    useEditingCellReporter(
        row.id,
        contentsValue?.content
            ? `${contentsValue.content.contentTypeGroup}-${contentsValue.content.contentType}`
            : undefined,
        isOpen,
    );

    // The read display is the design system's own preview: the cell only gains an edit affordance.
    if (!editableOpt) {
        return <DefaultRenderer />;
    }

    const { defaultLanguage } = contexts.language;
    const currentEntry =
        entries?.find(entry => entry.languageIsoCode === defaultLanguage) ?? entries?.[0];
    const currentHtml = String(currentEntry?.value?.[0] ?? '');

    const handleSave = async (edited: unknown) => {
        const html = typeof edited === 'string' ? edited : '';
        const nextEntries = writeLanguageEntry(entries ?? [], defaultLanguage, [html]);
        const saved = await commit(
            { values: nextEntries },
            intl.formatMessage({
                id: 'search-detail.edit-cell.html',
                defaultMessage: 'Rich text',
            }),
        );
        if (saved) {
            setEntries(nextEntries);
            setIsOpen(false);
        }
    };

    return (
        <Stack direction="row" alignItems="center" ref={anchorRef}>
            <DefaultRenderer />
            <IconButton
                aria-label={intl.formatMessage({
                    id: 'search-detail.edit-cell.html-edit',
                    defaultMessage: 'Edit rich text',
                })}
                size="small"
                color="primary"
                disabled={isLoading}
                onClick={e => {
                    e.stopPropagation();
                    setIsOpen(true);
                }}
            >
                <EditIcon fontSize="small" />
            </IconButton>
            <Popover
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={() => setIsOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Stack
                    onClick={e => {
                        e.stopPropagation();
                    }}
                    width="40rem"
                >
                    <StandaloneField
                        initialValue={currentHtml}
                        label={intl.formatMessage({
                            id: 'search-detail.edit-cell.html',
                            defaultMessage: 'Rich text',
                        })}
                        saving={isLoading}
                        onCancel={() => setIsOpen(false)}
                        onSave={handleSave}
                    >
                        {fieldProps => <HtmlFieldElement {...(fieldProps as any)} />}
                    </StandaloneField>
                </Stack>
            </Popover>
        </Stack>
    );
}
